import { io, Socket } from 'socket.io-client';
import { API_BASE_URL } from '../config/axiosConfig';
import { ChatMessage } from './chatService';
import { Notification } from './notificationService';
import { storage } from '../../utils/storage';

type MessageHandler = (message: ChatMessage) => void;
type NotificationHandler = (notification: Notification) => void;
type ErrorHandler = (error: Error) => void;
type ConnectionHandler = () => void;

class WebSocketService {
  private socket: Socket | null = null;
  private messageHandlers: Set<MessageHandler> = new Set();
  private notificationHandlers: Set<NotificationHandler> = new Set();
  private errorHandlers: Set<ErrorHandler> = new Set();
  private connectionHandlers: Set<ConnectionHandler> = new Set();
  private isConnecting = false;

  // Get WebSocket URL from API base URL
  private getWebSocketUrl(): string {
    // Convert HTTP/HTTPS URL to WS/WSS for Socket.IO
    // Socket.IO handles the protocol conversion automatically
    return API_BASE_URL;
  }

  connect(): void {
    if (this.socket?.connected || this.isConnecting) {
      return;
    }

    const token = storage.getAuthToken();
    if (!token) {
      console.error('No authentication token found');
      return;
    }

    this.isConnecting = true;

    try {
      const url = this.getWebSocketUrl();

      // Connect using Socket.IO with token in query parameter
      this.socket = io(`${url}/ws`, {
        auth: {
          token: token,
        },
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionAttempts: 5,
        reconnectionDelayMax: 5000,
        timeout: 20000,
      });

      // Connection events
      this.socket.on('connect', () => {
        console.log('✅ WebSocket (Socket.IO) connected');
        this.isConnecting = false;
        this.connectionHandlers.forEach(handler => handler());
      });

      this.socket.on('disconnect', reason => {
        console.log('❌ WebSocket disconnected:', reason);
        this.isConnecting = false;
      });

      this.socket.on('connect_error', error => {
        console.error('❌ WebSocket connection error:', error);
        this.isConnecting = false;
        this.errorHandlers.forEach(handler => handler(error));
      });

      // Message events - Socket.IO sends events directly
      this.socket.on('message', (data: any) => {
        try {
          // Handle both formats: direct message object or wrapped in { type, message }
          let message: ChatMessage;
          if (data.type === 'message' && data.message) {
            message = data.message as ChatMessage;
          } else {
            message = data as ChatMessage;
          }

          console.log('📨 WebSocket message event received:', message);
          this.messageHandlers.forEach(handler => handler(message));
        } catch (error) {
          console.error('Error handling message event:', error);
        }
      });

      // Notification events
      this.socket.on('notification', (data: any) => {
        try {
          // Handle both formats: direct notification object or wrapped in { type, notification }
          let notification: Notification;
          if (data.type === 'notification' && data.notification) {
            notification = data.notification as Notification;
          } else {
            notification = data as Notification;
          }

          console.log(
            '🔔 WebSocket notification event received:',
            notification
          );
          this.notificationHandlers.forEach(handler => handler(notification));
        } catch (error) {
          console.error('Error handling notification event:', error);
        }
      });
    } catch (error) {
      console.error('Error connecting WebSocket:', error);
      this.isConnecting = false;
    }
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.isConnecting = false;
  }

  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  // Subscribe to new messages
  onMessage(handler: MessageHandler): () => void {
    this.messageHandlers.add(handler);
    return () => this.messageHandlers.delete(handler);
  }

  // Subscribe to new notifications
  onNotification(handler: NotificationHandler): () => void {
    this.notificationHandlers.add(handler);
    return () => this.notificationHandlers.delete(handler);
  }

  // Subscribe to errors
  onError(handler: ErrorHandler): () => void {
    this.errorHandlers.add(handler);
    return () => this.errorHandlers.delete(handler);
  }

  // Subscribe to connection events
  onConnection(handler: ConnectionHandler): () => void {
    this.connectionHandlers.add(handler);
    return () => this.connectionHandlers.delete(handler);
  }

  // Join a conversation room (Socket.IO feature)
  joinConversation(conversationId: string): void {
    if (this.socket?.connected) {
      this.socket.emit(
        'join_conversation',
        { conversationId },
        (response: any) => {
          if (response?.success) {
            console.log('✅ Joined conversation room:', conversationId);
          } else {
            console.warn('⚠️ Failed to join conversation room:', response);
          }
        }
      );
    } else {
      console.warn('⚠️ Cannot join conversation room: WebSocket not connected');
    }
  }

  // Leave a conversation room (Socket.IO feature)
  leaveConversation(conversationId: string): void {
    if (this.socket?.connected) {
      this.socket.emit(
        'leave_conversation',
        { conversationId },
        (response: any) => {
          if (response?.success) {
            console.log('✅ Left conversation room:', conversationId);
          } else {
            console.warn('⚠️ Failed to leave conversation room:', response);
          }
        }
      );
    } else {
      console.warn(
        '⚠️ Cannot leave conversation room: WebSocket not connected'
      );
    }
  }

  // Test connection with ping/pong (Socket.IO feature)
  ping(): void {
    if (this.socket?.connected) {
      this.socket.emit('ping', {}, (response: any) => {
        console.log('🏓 Pong received:', response);
      });
    } else {
      console.warn('⚠️ Cannot ping: WebSocket not connected');
    }
  }

  // Send a message through WebSocket (Socket.IO emit)
  send(event: string, data: any): void {
    if (this.socket?.connected) {
      this.socket.emit(event, data);
    } else {
      console.warn('⚠️ WebSocket is not connected. Cannot send message.');
    }
  }
}

// Export singleton instance
export const websocketService = new WebSocketService();
